"use client";

import type * as React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface CInputProps extends React.ComponentProps<"input"> {
  /** Label displayed above the input */
  label?: string;
  /** Helper text displayed below the input */
  description?: string;
  /** Error message — replaces description and applies error styling */
  error?: string;
  /** Marks the field as required (adds * to label) */
  required?: boolean;
  /** Icon or element rendered inside the left side of the input */
  leftAddon?: React.ReactNode;
  /** Icon or interactive element (e.g. a password show/hide toggle) rendered inside the right side */
  rightAddon?: React.ReactNode;
  /** Wrapper className */
  wrapperClassName?: string;
}

/**
 * CInput — Design system input field.
 *
 * Renders: Label → Input (with optional left addon) → Description / Error
 *
 * States: default | focus (native) | disabled | error
 */
const CInput = ({
  label,
  description,
  error,
  required,
  leftAddon,
  rightAddon,
  id,
  className,
  wrapperClassName,
  disabled,
  ...props
}: CInputProps) => {
  const inputId =
    id ?? (label ? label.toLowerCase().replace(/\s+/g, "-") : undefined);
  const hasError = Boolean(error);

  return (
    <div className={cn("flex flex-col gap-1.5", wrapperClassName)}>
      {label && (
        <Label
          htmlFor={inputId}
          className={cn(
            required && "after:content-['*'] after:ml-0.5 after:text-error-500",
          )}
        >
          {label}
        </Label>
      )}

      <div className="relative">
        {leftAddon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
            {leftAddon}
          </div>
        )}
        <Input
          id={inputId}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={
            hasError
              ? `${inputId}-error`
              : description
                ? `${inputId}-description`
                : undefined
          }
          className={cn(
            "placeholder:text-text-tertiary",
            leftAddon && "pl-9",
            rightAddon && "pr-9",
            className,
          )}
          {...props}
        />
        {rightAddon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400">
            {rightAddon}
          </div>
        )}
      </div>

      {hasError ? (
        <p id={`${inputId}-error`} className="paragraph-xs text-error-500">
          {error}
        </p>
      ) : description ? (
        <p
          id={`${inputId}-description`}
          className="paragraph-xs text-neutral-500"
        >
          {description}
        </p>
      ) : null}
    </div>
  );
};

export default CInput;
export { CInput };
